import React, { useCallback, useMemo, useState, useRef, forwardRef } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    CircularProgress,
    Checkbox,
    Chip
} from '@mui/material';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { TableVirtuoso } from 'react-virtuoso';
import { useTranslation } from 'react-i18next';
import {
    formatBuyerColumnDisplay,
    formatGiReqPlannedDisplay,
    formatInvoiceInteger,
    formatInvoiceNoQtyLine,
    getInvoiceLineDisplayQuantity,
    sortInvoiceIssuanceRows,
    sortInvoiceLinesForDisplay
} from '../utils/invoiceUtils';
import {
    StyledTableCell,
    StyledTableRow
} from '../styles';

const getCurrencySymbol = (currency) => {
    switch (currency) {
        case 'USD':
            return '$';
        case 'KRW':
            return '₩';
        case 'IDR':
            return 'Rp';
        default:
            return currency;
    }
};

const SUB_ORDER_LINK_SX = {
    cursor: 'pointer',
    color: 'primary.main',
    textDecoration: 'underline',
    '&:hover': { color: 'primary.dark' }
};

/** INVOICE NO 열이 왼쪽 열 넘침 위에 안정적으로 클릭되도록 */
const INVOICE_NO_CELL_SX = {
    position: 'relative',
    zIndex: 2,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    // role=button·user-select:none 없이도 행 전체 드래그 선택에 포함되도록
    userSelect: 'text',
    WebkitUserSelect: 'text'
};

// 오더 현황 페이지 하단 Summary 스타일 참고
const SUMMARY_BOX_SX = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 3,
    flexWrap: 'wrap'
};
const BOLD_SPAN = { fontWeight: 'bold' };
const summaryRowSx = { bgcolor: '#f8fafc' };
const SUMMARY_CELL_SX = { py: 1.2 };

/** 동일 sub_order_id 연속 행에서 세로 병합할 컬럼 (현재 페이지·정렬 순서 기준) */
const SUB_ORDER_MERGE_COLUMN_IDS = [
    'customer',
    'subOrderId',
    'buyer',
    'manager',
    'style',
    'file',
    'dyeingNo',
    'delivery',
    'item',
    'size',
    'color',
    'price',
    'orderQty',
    'currency'
];

const sameInvoiceSubOrder = (a, b) => String(a ?? '') === String(b ?? '');

const COL_CHAR_PX = 9;
const COL_PADDING_PX = 32;
const COL_MAX_CHARS = 60;
const COL_MIN_WIDTH_PX = 60;

const getCellTextForColumn = (col, item) => {
    switch (col.id) {
        case 'customer': {
            const a = String(item?.vendor_name ?? '');
            const b = String(item?.sub_order_id ?? '');
            return a.length >= b.length ? a : b;
        }
        case 'subOrderId': return String(item?.sub_order_id ?? '');
        case 'buyer': return formatBuyerColumnDisplay(item?.buyer_name, item?.buyer_detail);
        case 'style': {
            const a = String(item?.style_no ?? '');
            const b = String(item?.file_no ?? '');
            return a.length >= b.length ? a : b;
        }
        case 'file': return String(item?.file_no ?? '');
        case 'dyeingNo': return String(item?.dyeing_no ?? '');
        case 'delivery': return String(item?.delivery_name ?? '');
        case 'manager': return String(item?.manager_name ?? '');
        case 'item': return String(item?.thread_name ?? '');
        case 'size': return String(item?.size_name ?? '');
        case 'color': return String(item?.color ?? '');
        case 'orderQty': return String(item?.order_qty ?? '');
        case 'price': return String(item?.price ?? '');
        case 'currency': return String(item?.price_currency ?? '');
        case 'no': return '999';
        case 'priceSum': {
            const unit = Number(item?.price);
            if (!Number.isFinite(unit)) return '';
            const sym = item?.price_currency ? `${getCurrencySymbol(item.price_currency)} ` : '';
            const qty = Number(item?.gi_request_line_qty || 0);
            return `${sym}${(qty * unit).toFixed(2)}`;
        }
        case 'giReqNo': return String(item?.gi_request_id ?? '');
        case 'giReqLineId': return String(item?.gi_request_line_id ?? '');
        case 'giReq':
            return item?.gi_request_planned_dispatch_at
                ? `${String(item.gi_request_planned_dispatch_at).slice(0, 10)} (${String(item?.gi_request_line_qty ?? '')})`
                : '';
        case 'packing': {
            const lines = Array.isArray(item?.packing_lot_lines) ? item.packing_lot_lines : [];
            if (lines.length === 0) return String(item?.packing ?? '');
            let longest = '';
            for (const ln of lines) {
                const s = `${String(ln?.expected_gi_at ?? '').slice(0, 10)} (${ln?.qty ?? ''})`;
                if (s.length > longest.length) longest = s;
            }
            return longest;
        }
        case 'giPost': {
            const lines = Array.isArray(item?.gi_post_lines) ? item.gi_post_lines : [];
            if (lines.length === 0) return String(item?.gi_post ?? '');
            let longest = '';
            for (const ln of lines) {
                const s = `${String(ln?.gi_time ?? '').slice(0, 10)} (${ln?.qty ?? ''})`;
                if (s.length > longest.length) longest = s;
            }
            return longest;
        }
        case 'invoiceNo': {
            const lines = Array.isArray(item?.invoice_lines) ? item.invoice_lines : [];
            if (lines.length === 0) {
                return String(item?.invoice_no ?? item?.existing_invoice_no ?? '');
            }
            let longest = '';
            for (const ln of lines) {
                const s = `${String(ln?.invoice_no ?? '')} (${ln?.qty ?? ln?.quantity ?? ''})`;
                if (s.length > longest.length) longest = s;
            }
            return longest;
        }
        default: return '';
    }
};

/** Invoice NO 표시 로직과 동일하게 sub_order 그룹에서 라인을 행별로 분배 */
const distributeInvoiceLinesBySubOrder = (rows) => {
    let invoiceLineMap = {};
    try {
        const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('invoiceLineMap') : null;
        if (raw) invoiceLineMap = JSON.parse(raw) || {};
    } catch (_e) {
        invoiceLineMap = {};
    }

    const getAllowedLineIdsForInvoice = (invoiceId) => {
        if (invoiceId == null) return null;
        const entry = invoiceLineMap[String(invoiceId)];
        if (!entry || !Array.isArray(entry.lines) || entry.lines.length === 0) return null;
        const allowed = new Set();
        for (const l of entry.lines) {
            if (l?.gi_request_line_id != null && l.gi_request_line_id !== '') {
                allowed.add(String(l.gi_request_line_id));
            }
        }
        return allowed.size > 0 ? allowed : null;
    };

    const isLineAllowedForRow = (line, row) => {
        const allowed = getAllowedLineIdsForInvoice(line?.invoice_id);
        if (!allowed) return true;
        const rowLineId = row?.gi_request_line_id;
        if (rowLineId == null || rowLineId === '') return false;
        return allowed.has(String(rowLineId));
    };

    const isRowOlderOrEqualToInvoice = (line, row) => {
        const invRaw = line?.invoice_date;
        const rowRaw = row?.gi_request_created_at ?? row?.gi_request_planned_dispatch_at;
        if (invRaw == null || invRaw === '' || rowRaw == null || rowRaw === '') return true;
        const toDay = (v) => {
            const s = String(v);
            const m = s.match(/^(\d{4})[-./](\d{1,2})[-./](\d{1,2})/);
            if (m) return Number(m[1]) * 10000 + Number(m[2]) * 100 + Number(m[3]);
            const d = new Date(v);
            return Number.isFinite(d.getTime())
                ? d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate()
                : NaN;
        };
        const invDay = toDay(invRaw);
        const rowDay = toDay(rowRaw);
        if (!Number.isFinite(invDay) || !Number.isFinite(rowDay)) return true;
        return rowDay <= invDay;
    };

    const isLineGiReqCompatibleWithRow = (line, row) => {
        const lnGiReqId = line?.gi_request_id;
        if (lnGiReqId != null && lnGiReqId !== '') {
            const rowGiReqId = row?.gi_request_id;
            if (rowGiReqId != null && rowGiReqId !== '' && String(lnGiReqId) !== String(rowGiReqId)) {
                return false;
            }
        }
        const lnLineId = line?.gi_request_line_id;
        if (lnLineId != null && lnLineId !== '') {
            const rowLineId = row?.gi_request_line_id;
            if (rowLineId != null && rowLineId !== '' && String(lnLineId) !== String(rowLineId)) {
                return false;
            }
        }
        return true;
    };

    const isRowEligibleForLine = (line, row) => {
        if (!isLineAllowedForRow(line, row)) return false;
        if (!isRowOlderOrEqualToInvoice(line, row)) return false;
        if (!isLineGiReqCompatibleWithRow(line, row)) return false;
        return true;
    };

    const result = {};
    const n = Array.isArray(rows) ? rows.length : 0;
    let i = 0;
    while (i < n) {
        const sid = rows[i]?.sub_order_id;
        let j = i + 1;
        while (j < n && sameInvoiceSubOrder(rows[j]?.sub_order_id, sid)) {
            j += 1;
        }
        const groupRows = j - i;
        const rawLines = Array.isArray(rows[i]?.invoice_lines) ? rows[i].invoice_lines : [];
        const seen = new Set();
        const filtered = [];
        let fallbackIdx = 0;
        for (const ln of rawLines) {
            if (!ln || typeof ln !== 'object') continue;
            const idPart = ln.invoice_id != null ? String(ln.invoice_id) : `noid:${fallbackIdx++}`;
            const qtyPart = ln.quantity ?? ln.qty ?? '';
            const noPart = ln.invoice_no ?? '';
            const lineIdPart = ln.gi_request_line_id ?? '';
            const key = `${idPart}|${qtyPart}|${noPart}|${lineIdPart}`;
            if (seen.has(key)) continue;
            seen.add(key);
            filtered.push(ln);
        }
        const baseLines = sortInvoiceLinesForDisplay(filtered);

        const rowAssignedMatched = Array.from({ length: groupRows }, () => []);
        const lineConsumed = new Array(baseLines.length).fill(false);

        for (let offset = 0; offset < groupRows; offset += 1) {
            const rowGiReqLineId = rows[i + offset]?.gi_request_line_id;
            if (rowGiReqLineId == null || rowGiReqLineId === '') continue;
            const k = String(rowGiReqLineId);
            for (let li = 0; li < baseLines.length; li += 1) {
                if (lineConsumed[li]) continue;
                if (!isLineAllowedForRow(baseLines[li], rows[i + offset])) continue;
                const lnId = baseLines[li]?.gi_request_line_id;
                if (lnId != null && lnId !== '' && String(lnId) === k) {
                    rowAssignedMatched[offset].push(baseLines[li]);
                    lineConsumed[li] = true;
                }
            }
        }

        const normalizeDate = (raw) => {
            if (raw == null || raw === '') return '';
            const s = String(raw);
            const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
            if (m) return m[1];
            const m2 = s.match(/^(\d{4})[./-](\d{1,2})[./-](\d{1,2})/);
            if (m2) {
                const yyyy = m2[1];
                const mm = String(m2[2]).padStart(2, '0');
                const dd = String(m2[3]).padStart(2, '0');
                return `${yyyy}-${mm}-${dd}`;
            }
            return s.trim();
        };
        const tryMatch = (predicate) => {
            for (let offset = 0; offset < groupRows; offset += 1) {
                if (rowAssignedMatched[offset].length > 0) continue;
                for (let li = 0; li < baseLines.length; li += 1) {
                    if (lineConsumed[li]) continue;
                    if (!isRowEligibleForLine(baseLines[li], rows[i + offset])) continue;
                    if (predicate(rows[i + offset], baseLines[li])) {
                        rowAssignedMatched[offset].push(baseLines[li]);
                        lineConsumed[li] = true;
                        break;
                    }
                }
            }
        };

        tryMatch((row, ln) => {
            const rowQty = Number(row?.gi_request_line_qty);
            const lnQty = Number(ln?.quantity ?? ln?.qty);
            if (!Number.isFinite(rowQty) || rowQty <= 0 || !Number.isFinite(lnQty)) return false;
            if (lnQty !== rowQty) return false;
            const rowDate = normalizeDate(row?.gi_request_planned_dispatch_at);
            const lnDate = normalizeDate(ln?.delivery_date);
            return rowDate !== '' && lnDate !== '' && rowDate === lnDate;
        });

        tryMatch((row, ln) => {
            const rowDate = normalizeDate(row?.gi_request_planned_dispatch_at);
            const lnDate = normalizeDate(ln?.delivery_date);
            return rowDate !== '' && lnDate !== '' && rowDate === lnDate;
        });

        tryMatch((row, ln) => {
            const rowQty = Number(row?.gi_request_line_qty);
            const lnQty = Number(ln?.quantity ?? ln?.qty);
            return Number.isFinite(rowQty) && rowQty > 0 && Number.isFinite(lnQty) && lnQty === rowQty;
        });

        const groupRowLineIds = new Set();
        const groupRowGiReqIds = new Set();
        for (let offset = 0; offset < groupRows; offset += 1) {
            const rid = rows[i + offset]?.gi_request_line_id;
            if (rid != null && rid !== '') groupRowLineIds.add(String(rid));
            const greq = rows[i + offset]?.gi_request_id;
            if (greq != null && greq !== '') groupRowGiReqIds.add(String(greq));
        }
        const unmatchedIndices = [];
        for (let li = 0; li < baseLines.length; li += 1) {
            if (lineConsumed[li]) continue;
            const lnLineId = baseLines[li]?.gi_request_line_id;
            if (lnLineId != null && lnLineId !== '' && !groupRowLineIds.has(String(lnLineId))) {
                continue;
            }
            const lnGiReqId = baseLines[li]?.gi_request_id;
            if (lnGiReqId != null && lnGiReqId !== '' && !groupRowGiReqIds.has(String(lnGiReqId))) {
                continue;
            }
            unmatchedIndices.push(li);
        }
        for (let offset = 0; offset < groupRows; offset += 1) {
            const rowIndex = i + offset;
            let assigned = rowAssignedMatched[offset];
            if (assigned.length === 0 && groupRows === 1) {
                if (offset < groupRows - 1) {
                    for (let u = 0; u < unmatchedIndices.length; u += 1) {
                        const li = unmatchedIndices[u];
                        if (li < 0) continue;
                        if (!isRowEligibleForLine(baseLines[li], rows[rowIndex])) continue;
                        assigned = [baseLines[li]];
                        unmatchedIndices[u] = -1;
                        break;
                    }
                } else {
                    const remaining = [];
                    for (let u = 0; u < unmatchedIndices.length; u += 1) {
                        const li = unmatchedIndices[u];
                        if (li < 0) continue;
                        if (!isRowEligibleForLine(baseLines[li], rows[rowIndex])) continue;
                        remaining.push(baseLines[li]);
                        unmatchedIndices[u] = -1;
                    }
                    assigned = remaining;
                }
            }
            result[rowIndex] = assigned;
        }
        i = j;
    }
    return result;
};

const InvoiceTable = ({
    invoiceData,
    page,
    rowsPerPage,
    totalCount,
    isLoading,
    isPackingLoading,
    isGiPostLoading,
    isInvoiceNoLoading,
    loadingMore = false,
    hasMore = false,
    onLoadMore,
    columnsToUse,
    checkedColumns,
    selectedItems,
    onSelectItem,
    onSelectAll,
    handleChangePage,
    handleChangeRowsPerPage,
    onSubOrderClick,
    onInvoicePreviewById,
    t,
    sortConfig: sortConfigProp,
    onSortConfigChange,
    hasSearched = false
}) => {
    const { i18n } = useTranslation();
    const [sortConfigLocal, setSortConfigLocal] = useState({ key: null, direction: null });
    const sortConfig =
        sortConfigProp !== undefined && onSortConfigChange ? sortConfigProp : sortConfigLocal;
    const setSortConfig = onSortConfigChange || setSortConfigLocal;
    const visibleColumns = useMemo(
        () => columnsToUse.filter(col => !col.meta && checkedColumns[col.id]),
        [columnsToUse, checkedColumns]
    );

    const issuedChipColumnId = useMemo(() => {
        if (visibleColumns.length === 0) return null;
        const subOrderIdx = columnsToUse.findIndex((c) => c.id === 'subOrderId');
        if (subOrderIdx < 0) return visibleColumns[0]?.id ?? null;
        const next = visibleColumns.find((c) => columnsToUse.findIndex((x) => x.id === c.id) >= subOrderIdx);
        return (next ?? visibleColumns[0])?.id ?? null;
    }, [visibleColumns, columnsToUse]);

    const sortedData = useMemo(
        () => sortInvoiceIssuanceRows(invoiceData, sortConfig),
        [invoiceData, sortConfig]
    );

    const handleSort = useCallback(
        (columnKey) => {
            setSortConfig((prev) => {
                if (prev.key === columnKey) {
                    if (prev.direction === 'asc') {
                        return { key: columnKey, direction: 'desc' };
                    }
                    if (prev.direction === 'desc') {
                        return { key: null, direction: null };
                    }
                }
                return { key: columnKey, direction: 'asc' };
            });
        },
        [setSortConfig]
    );

    const computedColumnInfo = useMemo(() => {
        const sampleSize = Math.min(sortedData.length, 200);
        const sample = sortedData.length > sampleSize ? sortedData.slice(0, sampleSize) : sortedData;
        return visibleColumns.map((col) => {
            let maxLen = String(col.label || '').length;
            for (const item of sample) {
                const text = getCellTextForColumn(col, item);
                if (text.length > maxLen) maxLen = text.length;
            }
            const cappedLen = Math.min(COL_MAX_CHARS, maxLen);
            const widthPx = Math.max(
                col.minWidth || COL_MIN_WIDTH_PX,
                Math.round(cappedLen * COL_CHAR_PX + COL_PADDING_PX)
            );
            return { id: col.id, widthPx, wrap: maxLen > COL_MAX_CHARS };
        });
    }, [visibleColumns, sortedData]);

    const tableSx = useMemo(() => {
        const wrapSelectors = {};
        computedColumnInfo.forEach((info, idx) => {
            if (info.wrap) {
                const nth = idx + 2;
                wrapSelectors[`& tbody td:nth-of-type(${nth})`] = {
                    whiteSpace: 'normal',
                    wordBreak: 'break-all',
                    overflowWrap: 'anywhere'
                };
            }
        });
        return {
            tableLayout: 'fixed',
            '& thead': {
                position: 'relative',
                zIndex: 100
            },
            '& thead th.MuiTableCell-head': {
                position: 'sticky',
                top: 0,
                zIndex: 101,
                borderRight: 'none'
            },
            '& thead th.MuiTableCell-head.sticky-left': {
                zIndex: 102
            },
            '& tbody tr': {
                transition: 'background-color 120ms ease',
                contain: 'layout style'
            },
            '& tbody tr:hover td.MuiTableCell-root': {
                backgroundColor: '#f8fafc'
            },
            '& tbody td.MuiTableCell-root': {
                verticalAlign: 'middle',
                borderRight: 'none',
                borderBottom: '1px solid #f1f5f9',
                fontSize: '0.875rem',
                padding: '14px 16px',
                textAlign: 'center'
            },
            '& tbody td.MuiTableCell-root.sticky-left, & thead th.MuiTableCell-head.sticky-left': {
                padding: 0,
                textAlign: 'center'
            },
            '& tbody td.MuiTableCell-root.sticky-left': {
                backgroundColor: '#ffffff !important'
            },
            '& tbody tr:hover td.MuiTableCell-root.sticky-left': {
                backgroundColor: '#f8fafc !important'
            },
            ...wrapSelectors
        };
    }, [computedColumnInfo]);

    const groupIndexByRow = useMemo(() => {
        const out = [];
        let prevSid = null;
        let g = -1;
        sortedData.forEach((row) => {
            const sid = String(row?.sub_order_id ?? '');
            if (sid !== prevSid) { g += 1; prevSid = sid; }
            out.push(g);
        });
        return out;
    }, [sortedData]);

    const formatPrice = (price) => {
        if (price === null || price === undefined || price === '') return '-';
        return parseFloat(price).toFixed(2);
    };

    const formatAmount = (amount) => {
        if (amount === null || amount === undefined || amount === '') return '-';
        const n = Number(amount);
        if (!Number.isFinite(n)) return '-';
        const langKey = String(i18n.language || 'en').split('-')[0];
        const locale = langKey === 'ko' ? 'ko-KR' : langKey === 'id' ? 'id-ID' : 'en-US';
        return new Intl.NumberFormat(locale, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(n);
    };

    const getGiReqQtyForLine = (row) => Number(row.gi_request_line_qty) || 0;

    const getPriceSum = (row) => {
        if (row.price == null || row.price === '') return null;
        const unit = Number(row.price);
        if (!Number.isFinite(unit)) return null;
        return getGiReqQtyForLine(row) * unit;
    };

    const getCellAlignment = (columnId) => {
        const centerAlignedColumns = [
            'no', 'giReqNo', 'giReqLineId', 'customer', 'subOrderId', 'buyer', 'style', 'file',
            'dyeingNo', 'delivery', 'manager', 'item', 'size', 'color', 'orderQty', 'price',
            'currency', 'giReq', 'packing', 'giPost', 'priceSum', 'invoiceNo'
        ];

        return centerAlignedColumns.includes(columnId) ? 'center' : 'left';
    };

    const currentPageData = sortedData;

    const mergeRowSpan = () => undefined;
    const skipMergedCell = () => false;
    const mergedCellSx = () => undefined;

    const packingDisplayLinesByRow = useMemo(() => {
        const result = {};
        const normalizeDate = (raw) => {
            if (raw == null || raw === '') return '';
            const m = String(raw).match(/^(\d{4}-\d{2}-\d{2})/);
            return m ? m[1] : '';
        };
        const rows = currentPageData;
        const n = rows.length;
        let i = 0;
        while (i < n) {
            const sid = rows[i]?.sub_order_id;
            let j = i + 1;
            while (j < n && sameInvoiceSubOrder(rows[j]?.sub_order_id, sid)) {
                j += 1;
            }
            const groupRows = j - i;
            const baseLines = Array.isArray(rows[i]?.packing_lot_lines)
                ? rows[i].packing_lot_lines
                : [];

            const usedLine = new Array(baseLines.length).fill(false);
            const assignedByOffset = new Array(groupRows).fill(null).map(() => []);

            for (let offset = 0; offset < groupRows; offset += 1) {
                const row = rows[i + offset];
                const rowDate = normalizeDate(row?.gi_request_planned_dispatch_at);
                const rowQty = Number(row?.gi_request_line_qty);
                if (!rowDate) continue;
                for (let li = 0; li < baseLines.length; li += 1) {
                    if (usedLine[li]) continue;
                    const ln = baseLines[li];
                    if (normalizeDate(ln?.expected_gi_at) !== rowDate) continue;
                    const lnQty = Number(ln?.qty);
                    if (Number.isFinite(rowQty) && rowQty > 0 && Number.isFinite(lnQty) && lnQty !== rowQty) continue;
                    assignedByOffset[offset].push(ln);
                    usedLine[li] = true;
                    break;
                }
            }

            for (let offset = 0; offset < groupRows; offset += 1) {
                if (assignedByOffset[offset].length > 0) continue;
                const row = rows[i + offset];
                const rowDate = normalizeDate(row?.gi_request_planned_dispatch_at);
                if (!rowDate) continue;
                for (let li = 0; li < baseLines.length; li += 1) {
                    if (usedLine[li]) continue;
                    if (normalizeDate(baseLines[li]?.expected_gi_at) !== rowDate) continue;
                    assignedByOffset[offset].push(baseLines[li]);
                    usedLine[li] = true;
                    break;
                }
            }

            const leftover = [];
            for (let li = 0; li < baseLines.length; li += 1) {
                if (!usedLine[li]) leftover.push(baseLines[li]);
            }
            if (leftover.length > 0 && groupRows === 1) {
                const emptyOffsets = [];
                for (let offset = 0; offset < groupRows; offset += 1) {
                    if (assignedByOffset[offset].length === 0) emptyOffsets.push(offset);
                }
                if (emptyOffsets.length === 1) {

                    assignedByOffset[emptyOffsets[0]] = assignedByOffset[emptyOffsets[0]].concat(leftover);
                } else if (emptyOffsets.length > 1) {

                    for (let k = 0; k < emptyOffsets.length; k += 1) {
                        const off = emptyOffsets[k];
                        if (k < emptyOffsets.length - 1) {
                            if (k < leftover.length) assignedByOffset[off] = [leftover[k]];
                        } else {
                            assignedByOffset[off] = leftover.slice(k);
                        }
                    }
                }
            }

            for (let offset = 0; offset < groupRows; offset += 1) {
                result[i + offset] = assignedByOffset[offset];
            }

            i = j;
        }
        return result;
    }, [currentPageData]);

    const giPostDisplayLinesByRow = useMemo(() => {
        const result = {};
        const normalizeDate = (raw) => {
            if (raw == null || raw === '') return '';
            const m = String(raw).match(/^(\d{4}-\d{2}-\d{2})/);
            return m ? m[1] : '';
        };
        const rows = currentPageData;
        const n = rows.length;
        let i = 0;
        while (i < n) {
            const sid = rows[i]?.sub_order_id;
            let j = i + 1;
            while (j < n && sameInvoiceSubOrder(rows[j]?.sub_order_id, sid)) {
                j += 1;
            }
            const groupRows = j - i;
            const baseLines = Array.isArray(rows[i]?.gi_post_lines)
                ? rows[i].gi_post_lines
                : [];

            const usedLine = new Array(baseLines.length).fill(false);
            const assignedByOffset = new Array(groupRows).fill(null).map(() => []);

            for (let offset = 0; offset < groupRows; offset += 1) {
                const row = rows[i + offset];
                const rowDate = normalizeDate(row?.gi_request_planned_dispatch_at);
                const rowQty = Number(row?.gi_request_line_qty);
                if (!rowDate) continue;
                for (let li = 0; li < baseLines.length; li += 1) {
                    if (usedLine[li]) continue;
                    const ln = baseLines[li];
                    if (normalizeDate(ln?.gi_time) !== rowDate) continue;
                    const lnQty = Number(ln?.qty);
                    if (Number.isFinite(rowQty) && rowQty > 0 && Number.isFinite(lnQty) && lnQty !== rowQty) continue;
                    assignedByOffset[offset].push(ln);
                    usedLine[li] = true;
                    break;
                }
            }

            for (let offset = 0; offset < groupRows; offset += 1) {
                if (assignedByOffset[offset].length > 0) continue;
                const row = rows[i + offset];
                const rowDate = normalizeDate(row?.gi_request_planned_dispatch_at);
                if (!rowDate) continue;
                for (let li = 0; li < baseLines.length; li += 1) {
                    if (usedLine[li]) continue;
                    if (normalizeDate(baseLines[li]?.gi_time) !== rowDate) continue;
                    assignedByOffset[offset].push(baseLines[li]);
                    usedLine[li] = true;
                    break;
                }
            }

            const leftover = [];
            for (let li = 0; li < baseLines.length; li += 1) {
                if (!usedLine[li]) leftover.push(baseLines[li]);
            }
            if (leftover.length > 0 && groupRows === 1) {
                const emptyOffsets = [];
                for (let offset = 0; offset < groupRows; offset += 1) {
                    if (assignedByOffset[offset].length === 0) emptyOffsets.push(offset);
                }
                if (emptyOffsets.length === 1) {
                    assignedByOffset[emptyOffsets[0]] = assignedByOffset[emptyOffsets[0]].concat(leftover);
                } else if (emptyOffsets.length > 1) {
                    for (let k = 0; k < emptyOffsets.length; k += 1) {
                        const off = emptyOffsets[k];
                        if (k < emptyOffsets.length - 1) {
                            if (k < leftover.length) assignedByOffset[off] = [leftover[k]];
                        } else {
                            assignedByOffset[off] = leftover.slice(k);
                        }
                    }
                }
            }

            for (let offset = 0; offset < groupRows; offset += 1) {
                result[i + offset] = assignedByOffset[offset];
            }

            i = j;
        }
        return result;
    }, [currentPageData]);

    /** PACKING과 동일: 동일 sub_order 연속 행에서 인보이스 라인을 행별로 나누고, 마지막 행에 남은 라인을 한꺼번에 표시 */
    const invoiceDisplayLinesByRow = useMemo(() => {
        return distributeInvoiceLinesBySubOrder(currentPageData);
    }, [currentPageData]);

    const invoicedFlagByIndex = useMemo(() => {
        return currentPageData.map((item, idx) => {
            if (item?.existing_invoice_no || item?.invoice_no) return true;
            const distributed = invoiceDisplayLinesByRow[idx];
            if (Array.isArray(distributed) && distributed.length > 0) return true;
            return false;
        });
    }, [currentPageData, invoiceDisplayLinesByRow]);

    const selectableIds = useMemo(
        () => currentPageData.filter((item, idx) => !invoicedFlagByIndex[idx]).map((item) => item.id),
        [currentPageData, invoicedFlagByIndex]
    );
    const selectedItemsSet = useMemo(() => new Set(selectedItems), [selectedItems]);
    const selectedOnPageCount = useMemo(() => {
        let n = 0;
        for (const id of selectableIds) if (selectedItemsSet.has(id)) n += 1;
        return n;
    }, [selectableIds, selectedItemsSet]);

    // Virtuoso components를 안정 유지 — data append 시 components 재생성이 스크롤을 첫 행으로 리셋함
    const invoicedFlagByIndexRef = useRef(invoicedFlagByIndex);
    invoicedFlagByIndexRef.current = invoicedFlagByIndex;
    const computedColumnInfoRef = useRef(computedColumnInfo);
    computedColumnInfoRef.current = computedColumnInfo;
    const tableSxRef = useRef(tableSx);
    tableSxRef.current = tableSx;

    const isAllSelected = selectableIds.length > 0 && selectedOnPageCount === selectableIds.length;
    const isIndeterminate = selectedOnPageCount > 0 && selectedOnPageCount < selectableIds.length;

    const headerContent = () => (
        <TableRow>
            <StyledTableCell
                align="center"
                className="sticky-left"
            >
                <Checkbox
                    checked={isAllSelected}
                    indeterminate={isIndeterminate}
                    onChange={(e) => onSelectAll(e.target.checked, selectableIds)}
                    disabled={selectableIds.length === 0}
                    size="small"
                    sx={{
                        color: '#ffffff',
                        '&.Mui-checked': { color: '#ffffff' },
                        '&.MuiCheckbox-indeterminate': { color: '#ffffff' },
                        '& .MuiSvgIcon-root': { color: '#ffffff' },
                        '&.Mui-disabled .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.4)' }
                    }}
                />
            </StyledTableCell>
            {visibleColumns.map((column) => (
                column.id === 'no' ? (
                    <StyledTableCell
                        key={column.id}
                        align="center"
                        style={{ minWidth: column.minWidth }}
                    >
                        {column.label}
                    </StyledTableCell>
                ) : (
                    <StyledTableCell
                        key={column.id}
                        align="center"
                        style={{ minWidth: column.minWidth }}
                        onClick={() => handleSort(column.id)}
                        sx={{
                            cursor: 'pointer',
                            userSelect: 'none',
                            '&:hover': {
                                backgroundColor: (theme) => theme.palette.primary.dark
                            }
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            {column.label}
                            {sortConfig.key === column.id && sortConfig.direction === 'asc' && (
                                <ArrowUpward sx={{ fontSize: '0.9rem' }} />
                            )}
                            {sortConfig.key === column.id && sortConfig.direction === 'desc' && (
                                <ArrowDownward sx={{ fontSize: '0.9rem' }} />
                            )}
                        </Box>
                    </StyledTableCell>
                )
            ))}
        </TableRow>
    );

    const renderRowCells = (index, item) => {
        const isSelected = selectedItemsSet.has(item.id);
        const isInvoiced = !!invoicedFlagByIndex[index];
        const currencySymbol = getCurrencySymbol(item.price_currency);
        const groupIdx = groupIndexByRow[index] ?? 0;
        const isFirstOfGroup = index === 0 || groupIndexByRow[index - 1] !== groupIdx;

        return (
            <>
                <StyledTableCell
                    align="center"
                    className="sticky-left"
                >
                    <Checkbox
                        checked={isSelected}
                        onChange={(e) => onSelectItem(item.id, e.target.checked)}
                        disabled={isInvoiced}
                        size="small"
                        sx={{
                            color: '#1976d2',
                            '&.Mui-checked': { color: '#1976d2' }
                        }}
                    />
                </StyledTableCell>
                {visibleColumns.map(column => {
                                            const cellElement = (() => {
                                            switch (column.id) {
                                                case 'no':
                                                    return <StyledTableCell key={column.id} align={getCellAlignment(column.id)}>{index + 1}</StyledTableCell>;
                                                case 'giReqNo':
                                                    return <StyledTableCell key={column.id} align={getCellAlignment(column.id)}>{item.gi_request_id != null && item.gi_request_id !== '' ? `GI-REQ-${String(item.gi_request_id).padStart(3, '0')}` : '-'}</StyledTableCell>;
                                                case 'giReqLineId':
                                                    return <StyledTableCell key={column.id} align={getCellAlignment(column.id)}>{item.gi_request_line_id ?? '-'}</StyledTableCell>;
                                                case 'customer':
                                                    if (skipMergedCell(index, 'customer')) return null;
                                                    return (
                                                        <StyledTableCell
                                                            key={column.id}
                                                            align={getCellAlignment(column.id)}
                                                            rowSpan={mergeRowSpan(index, 'customer')}
                                                            sx={mergedCellSx(index, 'customer')}
                                                        >
                                                            {isFirstOfGroup ? (item.vendor_name || '-') : ''}
                                                        </StyledTableCell>
                                                    );
                                                case 'subOrderId':
                                                    if (skipMergedCell(index, 'subOrderId')) return null;
                                                    return (
                                                        <StyledTableCell
                                                            key={column.id}
                                                            align={getCellAlignment(column.id)}
                                                            rowSpan={mergeRowSpan(index, 'subOrderId')}
                                                            sx={mergedCellSx(index, 'subOrderId') || {}}
                                                        >
                                                            <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                                                {isFirstOfGroup ? (
                                                                    <Box
                                                                        component="span"
                                                                        onClick={
                                                                            onSubOrderClick &&
                                                                            item.sub_order_id != null &&
                                                                            item.sub_order_id !== ''
                                                                                ? (e) => {
                                                                                      e.stopPropagation();
                                                                                      onSubOrderClick(item.sub_order_id);
                                                                                  }
                                                                                : undefined
                                                                        }
                                                                        sx={
                                                                            onSubOrderClick &&
                                                                            item.sub_order_id != null &&
                                                                            item.sub_order_id !== ''
                                                                                ? SUB_ORDER_LINK_SX
                                                                                : undefined
                                                                        }
                                                                    >
                                                                        {item.sub_order_id ?? '-'}
                                                                    </Box>
                                                                ) : null}
                                                                {isInvoiced && issuedChipColumnId === 'subOrderId' && (
                                                                    <Chip
                                                                        size="small"
                                                                        icon={<CheckCircleIcon sx={{ fontSize: '0.85rem' }} />}
                                                                        label={t('invoice.row.issued', '발급완료')}
                                                                        sx={{
                                                                            height: 20,
                                                                            fontSize: '0.65rem',
                                                                            backgroundColor: '#10b981',
                                                                            color: '#ffffff',
                                                                            '& .MuiChip-icon': { color: '#ffffff' }
                                                                        }}
                                                                    />
                                                                )}
                                                            </Box>
                                                        </StyledTableCell>
                                                    );
                                                case 'buyer':
                                                    if (skipMergedCell(index, 'buyer')) return null;
                                                    return (
                                                        <StyledTableCell
                                                            key={column.id}
                                                            align={getCellAlignment(column.id)}
                                                            rowSpan={mergeRowSpan(index, 'buyer')}
                                                            sx={mergedCellSx(index, 'buyer')}
                                                        >
                                                            {isFirstOfGroup ? (formatBuyerColumnDisplay(item.buyer_name, item.buyer_detail) || '-') : ''}
                                                        </StyledTableCell>
                                                    );
                                                case 'style':
                                                    if (skipMergedCell(index, 'style')) return null;
                                                    return (
                                                        <StyledTableCell
                                                            key={column.id}
                                                            align={getCellAlignment(column.id)}
                                                            rowSpan={mergeRowSpan(index, 'style')}
                                                            sx={mergedCellSx(index, 'style')}
                                                        >
                                                            {isFirstOfGroup ? (item.style_no ?? '-') : ''}
                                                        </StyledTableCell>
                                                    );
                                                case 'file':
                                                    if (skipMergedCell(index, 'file')) return null;
                                                    return (
                                                        <StyledTableCell
                                                            key={column.id}
                                                            align={getCellAlignment(column.id)}
                                                            rowSpan={mergeRowSpan(index, 'file')}
                                                            sx={mergedCellSx(index, 'file')}
                                                        >
                                                            {isFirstOfGroup ? (item.file_no ?? '-') : ''}
                                                        </StyledTableCell>
                                                    );
                                                case 'dyeingNo':
                                                    if (skipMergedCell(index, 'dyeingNo')) return null;
                                                    return (
                                                        <StyledTableCell
                                                            key={column.id}
                                                            align={getCellAlignment(column.id)}
                                                            rowSpan={mergeRowSpan(index, 'dyeingNo')}
                                                            sx={mergedCellSx(index, 'dyeingNo')}
                                                        >
                                                            {isFirstOfGroup
                                                                ? (item.dyeing_no != null && item.dyeing_no !== ''
                                                                    ? String(item.dyeing_no)
                                                                    : '-')
                                                                : ''}
                                                        </StyledTableCell>
                                                    );
                                                case 'delivery':
                                                    if (skipMergedCell(index, 'delivery')) return null;
                                                    return (
                                                        <StyledTableCell
                                                            key={column.id}
                                                            align={getCellAlignment(column.id)}
                                                            rowSpan={mergeRowSpan(index, 'delivery')}
                                                            sx={mergedCellSx(index, 'delivery')}
                                                        >
                                                            {isFirstOfGroup ? (item.delivery_name || '-') : ''}
                                                        </StyledTableCell>
                                                    );
                                                case 'manager':
                                                    if (skipMergedCell(index, 'manager')) return null;
                                                    return (
                                                        <StyledTableCell
                                                            key={column.id}
                                                            align={getCellAlignment(column.id)}
                                                            rowSpan={mergeRowSpan(index, 'manager')}
                                                            sx={mergedCellSx(index, 'manager')}
                                                        >
                                                            {isFirstOfGroup ? (item.manager_name || '-') : ''}
                                                        </StyledTableCell>
                                                    );
                                                case 'item':
                                                    if (skipMergedCell(index, 'item')) return null;
                                                    return (
                                                        <StyledTableCell
                                                            key={column.id}
                                                            align={getCellAlignment(column.id)}
                                                            rowSpan={mergeRowSpan(index, 'item')}
                                                            sx={mergedCellSx(index, 'item')}
                                                        >
                                                            {isFirstOfGroup ? (item.thread_name || '-') : ''}
                                                        </StyledTableCell>
                                                    );
                                                case 'size':
                                                    if (skipMergedCell(index, 'size')) return null;
                                                    return (
                                                        <StyledTableCell
                                                            key={column.id}
                                                            align={getCellAlignment(column.id)}
                                                            rowSpan={mergeRowSpan(index, 'size')}
                                                            sx={mergedCellSx(index, 'size')}
                                                        >
                                                            {isFirstOfGroup ? (item.size_name || '-') : ''}
                                                        </StyledTableCell>
                                                    );
                                                case 'color':
                                                    if (skipMergedCell(index, 'color')) return null;
                                                    return (
                                                        <StyledTableCell
                                                            key={column.id}
                                                            align={getCellAlignment(column.id)}
                                                            rowSpan={mergeRowSpan(index, 'color')}
                                                            sx={mergedCellSx(index, 'color')}
                                                        >
                                                            {isFirstOfGroup ? (item.color || '-') : ''}
                                                        </StyledTableCell>
                                                    );
                                                case 'orderQty':
                                                    if (skipMergedCell(index, 'orderQty')) return null;
                                                    return (
                                                        <StyledTableCell
                                                            key={column.id}
                                                            align={getCellAlignment(column.id)}
                                                            rowSpan={mergeRowSpan(index, 'orderQty')}
                                                            sx={mergedCellSx(index, 'orderQty')}
                                                        >
                                                            {isFirstOfGroup
                                                                ? (item.order_qty != null && item.order_qty !== ''
                                                                    ? formatInvoiceInteger(item.order_qty, i18n.language) ?? '-'
                                                                    : '-')
                                                                : ''}
                                                        </StyledTableCell>
                                                    );
                                                case 'price':
                                                    if (skipMergedCell(index, 'price')) return null;
                                                    return (
                                                        <StyledTableCell
                                                            key={column.id}
                                                            align={getCellAlignment(column.id)}
                                                            rowSpan={mergeRowSpan(index, 'price')}
                                                            sx={mergedCellSx(index, 'price')}
                                                        >
                                                            {isFirstOfGroup ? (
                                                                <Box>
                                                                    <Typography component="span" variant="body2" fontWeight="medium" color="primary.main" mr={1}>
                                                                        {item.price_currency ? currencySymbol : ''}
                                                                    </Typography>
                                                                    {formatPrice(item.price)}
                                                                </Box>
                                                            ) : ''}
                                                        </StyledTableCell>
                                                    );
                                                case 'currency':
                                                    if (skipMergedCell(index, 'currency')) return null;
                                                    return (
                                                        <StyledTableCell
                                                            key={column.id}
                                                            align={getCellAlignment(column.id)}
                                                            rowSpan={mergeRowSpan(index, 'currency')}
                                                            sx={mergedCellSx(index, 'currency')}
                                                        >
                                                            {isFirstOfGroup ? (item.price_currency || '-') : ''}
                                                        </StyledTableCell>
                                                    );
                                                case 'giReq':
                                                    return (
                                                        <StyledTableCell key={column.id} align={getCellAlignment(column.id)}>
                                                            {isPackingLoading || isGiPostLoading ? (
                                                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                                                    <CircularProgress size={16} />
                                                                </Box>
                                                            ) : (
                                                                <Typography variant="body2" component="span" sx={{ whiteSpace: 'nowrap' }}>
                                                                    {formatGiReqPlannedDisplay(
                                                                        item.gi_request_planned_dispatch_at,
                                                                        item.gi_request_line_qty,
                                                                        i18n.language
                                                                    )}
                                                                </Typography>
                                                            )}
                                                        </StyledTableCell>
                                                    );
                                                case 'packing': {
                                                    const pl = packingDisplayLinesByRow[index] || [];
                                                    const hasLines = Array.isArray(pl) && pl.length > 0;
                                                    return (
                                                        <StyledTableCell key={column.id} align={getCellAlignment(column.id)}>
                                                            {isPackingLoading ? (
                                                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                                                    <CircularProgress size={16} />
                                                                </Box>
                                                            ) : hasLines ? (
                                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.35, py: 0.25 }}>
                                                                    {pl.map((ln, idx) => (
                                                                        <span key={`${item.id}-p-${idx}`} style={{ lineHeight: 1.35 }}>
                                                                            {formatGiReqPlannedDisplay(ln.expected_gi_at, ln.qty, i18n.language)}
                                                                        </span>
                                                                    ))}
                                                                </Box>
                                                            ) : (
                                                                item.packing || '-'
                                                            )}
                                                        </StyledTableCell>
                                                    );
                                                }
                                                case 'giPost': {
                                                    const gpl = giPostDisplayLinesByRow[index] || [];
                                                    const hasGpLines = Array.isArray(gpl) && gpl.length > 0;
                                                    return (
                                                        <StyledTableCell
                                                            key={column.id}
                                                            align={getCellAlignment(column.id)}
                                                        >
                                                            {isGiPostLoading ? (
                                                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                                                    <CircularProgress size={16} />
                                                                </Box>
                                                            ) : hasGpLines ? (
                                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.35, py: 0.25 }}>
                                                                    {gpl.map((ln, idx) => (
                                                                        <span key={`${item.id}-g-${idx}`} style={{ lineHeight: 1.35 }}>
                                                                            {formatGiReqPlannedDisplay(ln.gi_time, ln.qty, i18n.language)}
                                                                        </span>
                                                                    ))}
                                                                </Box>
                                                            ) : (
                                                                item.gi_post || '-'
                                                            )}
                                                        </StyledTableCell>
                                                    );
                                                }
                                                case 'priceSum': {
                                                    const sum = getPriceSum(item);
                                                    return (
                                                        <StyledTableCell key={column.id} align={getCellAlignment(column.id)}>
                                                            {sum != null && Number.isFinite(sum) ? (
                                                                <Box>
                                                                    <Typography component="span" variant="body2" fontWeight="medium" color="primary.main" mr={1}>
                                                                        {item.price_currency ? currencySymbol : ''}
                                                                    </Typography>
                                                                    {formatPrice(sum)}
                                                                </Box>
                                                            ) : (
                                                                '-'
                                                            )}
                                                        </StyledTableCell>
                                                    );
                                                }
                                                case 'invoiceNo': {
                                                    const invPl =
                                                        invoiceDisplayLinesByRow[index] || [];
                                                    const hasLines = invPl.length > 0;
                                                    const firstLine = hasLines ? invPl[0] : null;
                                                    const firstInvId = firstLine?.invoice_id;
                                                    const firstClickable =
                                                        onInvoicePreviewById &&
                                                        firstInvId != null &&
                                                        Number.isFinite(Number(firstInvId));
                                                    if (
                                                        hasLines &&
                                                        invPl.length === 1 &&
                                                        firstClickable
                                                    ) {
                                                        const singleLabel = formatInvoiceNoQtyLine(
                                                            firstLine.invoice_no,
                                                            getInvoiceLineDisplayQuantity(firstLine),
                                                            i18n.language
                                                        );
                                                        return (
                                                            <StyledTableCell
                                                                key={column.id}
                                                                align={getCellAlignment(column.id)}
                                                                sx={{
                                                                    ...INVOICE_NO_CELL_SX,
                                                                    ...SUB_ORDER_LINK_SX
                                                                }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    onInvoicePreviewById(firstInvId);
                                                                }}
                                                            >
                                                                {singleLabel}
                                                            </StyledTableCell>
                                                        );
                                                    }
                                                    return (
                                                        <StyledTableCell
                                                            key={column.id}
                                                            align={getCellAlignment(column.id)}
                                                            sx={INVOICE_NO_CELL_SX}
                                                        >
                                                            {isInvoiceNoLoading ? (
                                                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                                                    <CircularProgress size={16} />
                                                                </Box>
                                                            ) : hasLines ? (
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        alignItems: 'center',
                                                                        gap: 0.35,
                                                                        py: 0.25
                                                                    }}
                                                                >
                                                                    {invPl.map((ln, idx) => {
                                                                        const invId = ln.invoice_id;
                                                                        const clickable =
                                                                            onInvoicePreviewById &&
                                                                            invId != null &&
                                                                            Number.isFinite(Number(invId));
                                                                        const label = formatInvoiceNoQtyLine(
                                                                            ln.invoice_no,
                                                                            getInvoiceLineDisplayQuantity(ln),
                                                                            i18n.language
                                                                        );
                                                                        return clickable ? (
                                                                            <Box
                                                                                key={`${item.id}-inv-${idx}`}
                                                                                component="span"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    onInvoicePreviewById(invId);
                                                                                }}
                                                                                sx={SUB_ORDER_LINK_SX}
                                                                            >
                                                                                {label}
                                                                            </Box>
                                                                        ) : (
                                                                            <span key={`${item.id}-inv-${idx}`} style={{ color: '#1976d2' }}>{label}</span>
                                                                        );
                                                                    })}
                                                                </Box>
                                                            ) : (item.invoice_no || item.existing_invoice_no) ? (
                                                                <span style={{ color: '#1976d2' }}>
                                                                    {item.invoice_no || item.existing_invoice_no}
                                                                </span>
                                                            ) : (
                                                                '-'
                                                            )}
                                                        </StyledTableCell>
                                                    );
                                                }
                                                default:
                                                    return <StyledTableCell key={column.id} align={getCellAlignment(column.id)}>-</StyledTableCell>;
                                            }
                                            })();

                                            if (
                                                cellElement &&
                                                isInvoiced &&
                                                issuedChipColumnId &&
                                                issuedChipColumnId !== 'subOrderId' &&
                                                column.id === issuedChipColumnId
                                            ) {
                                                return React.cloneElement(
                                                    cellElement,
                                                    {},
                                                    <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                                        <Box component="span">{cellElement.props.children}</Box>
                                                        <Chip
                                                            size="small"
                                                            icon={<CheckCircleIcon sx={{ fontSize: '0.85rem' }} />}
                                                            label={t('invoice.row.issued', '발급완료')}
                                                            sx={{
                                                                height: 20,
                                                                fontSize: '0.65rem',
                                                                backgroundColor: '#10b981',
                                                                color: '#ffffff',
                                                                '& .MuiChip-icon': { color: '#ffffff' }
                                                            }}
                                                        />
                                                    </Box>
                                                );
                                            }
                                            return cellElement;
                                        })}
            </>
        );
    };

    const VirtuosoTableComponents = useMemo(() => ({
        Scroller: forwardRef((props, ref) => (
            <TableContainer component={Paper} {...props} ref={ref} />
        )),
        Table: (props) => (
            <Table {...props} stickyHeader size="small" sx={tableSxRef.current}>
                <colgroup>
                    <col style={{ width: 60 }} />
                    {computedColumnInfoRef.current.map((info) => (
                        <col key={info.id} style={{ width: info.widthPx }} />
                    ))}
                </colgroup>
                {props.children}
            </Table>
        ),
        TableHead: forwardRef(({ style, ...props }, ref) => (
            <TableHead {...props} ref={ref} style={{ ...style, zIndex: 100, position: 'relative' }} />
        )),
        TableRow: forwardRef((props, ref) => {
            const idx = Number(props['data-item-index']);
            const invoiced = Number.isFinite(idx) && invoicedFlagByIndexRef.current[idx];
            return (
                <StyledTableRow
                    {...props}
                    ref={ref}
                    sx={invoiced ? {
                        opacity: 0.75,
                        '& td.MuiTableCell-root': {
                            color: 'text.secondary',
                            borderBottom: '1px solid #94a3b8',
                            backgroundColor: '#cbd5e1 !important'
                        },
                        '&:hover td.MuiTableCell-root': {
                            backgroundColor: '#b8c5d3 !important'
                        }
                    } : undefined}
                />
            );
        }),
        TableBody: forwardRef((props, ref) => <TableBody {...props} ref={ref} />)
    }), []);

    // 행 수에 따라 높이 바꾸면 Virtuoso 스크롤이 리셋되므로 고정
    const tableHeightStyle = 'calc(100vh - 280px)';

    return (
        <Box sx={{ position: 'relative' }}>
            {isLoading && sortedData.length > 0 && (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 200,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.6)',
                        backdropFilter: 'blur(1px)'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1, borderRadius: 1, backgroundColor: '#ffffff', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}>
                        <CircularProgress size={20} />
                        <Typography variant="body2" color="text.secondary">
                            {t('common.loading')}
                        </Typography>
                    </Box>
                </Box>
            )}
            {sortedData.length > 0 ? (
                <>
                    <TableVirtuoso
                        style={{ height: tableHeightStyle, willChange: 'transform' }}
                        data={currentPageData}
                        components={VirtuosoTableComponents}
                        fixedHeaderContent={headerContent}
                        itemContent={renderRowCells}
                        computeItemKey={(index, item) => item?.id ?? index}
                        defaultItemHeight={60}
                        increaseViewportBy={{ top: 400, bottom: 800 }}
                        overscan={{ main: 400, reverse: 400 }}
                        endReached={() => {
                            if (!hasSearched || !hasMore || isLoading || loadingMore) return;
                            onLoadMore?.();
                        }}
                    />
                    {(loadingMore || (!hasMore && sortedData.length > 0)) && (
                        <Box sx={{ py: 1.5, textAlign: 'center' }}>
                            {loadingMore ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={16} />
                                    <Typography variant="body2" color="text.secondary">
                                        {t('common.loading')}
                                    </Typography>
                                </Box>
                            ) : (
                                <Typography variant="caption" color="text.secondary">
                                    {t('common.listLoadCount', {
                                        loaded: sortedData.length,
                                        total: Number.isFinite(totalCount) ? totalCount : sortedData.length
                                    })}
                                </Typography>
                            )}
                        </Box>
                    )}
                </>
            ) : (
                <TableContainer component={Paper}>
                    <Table stickyHeader size="small" sx={tableSx}>
                        <colgroup>
                            <col style={{ width: 60 }} />
                            {computedColumnInfo.map((info) => (
                                <col key={info.id} style={{ width: info.widthPx }} />
                            ))}
                        </colgroup>
                        <TableHead>{headerContent()}</TableHead>
                        <TableBody>
                            <StyledTableRow>
                                <StyledTableCell colSpan={visibleColumns.length + 1} align="center" sx={{ whiteSpace: 'normal' }}>
                                    {isLoading ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5, py: 6 }}>
                                            <CircularProgress size={24} />
                                            <Typography color="text.secondary">{t('common.loading')}</Typography>
                                        </Box>
                                    ) : (
                                        <Box sx={{ textAlign: 'center', py: 6, fontSize: '1rem', color: 'text.secondary' }}>
                                            {hasSearched ? t('common.noData') : t('common.searchGuide', '검색하여 데이터를 확인하세요')}
                                        </Box>
                                    )}
                                </StyledTableCell>
                            </StyledTableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default InvoiceTable;
